import React, { useState, useRef, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import DatePicker from 'react-datepicker';
import { parse, format } from 'date-fns';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';
import { useQuery, useApolloClient } from 'react-apollo';

import Spinner from '../../../../components/global/Spinner';
import { AVATAR_SIZE_72 } from '../../../../constants/avatar-sizes';
import Button from '../../../../components/global/Button';
import Alert from '../../../../components/global/Alert';
import IdentityAvatarContainer from '../../../../containers/global/identity/IdentityAvatarContainer';
import FormGroup from '../../../../components/global/FormGroup';
import useRosterIdentityUpdate from './useRosterIdentityUpdate';
import UserAddressFormModal from '../../../../components/global/user-address/UserAddressFormModal';
import UserAddressDisplayEditBox from '../../../../components/global/user-address/UserAddressDisplayEditBox';
import PhoneInput from '../../../../components/global/PhoneInput';
import { isBlankPhone, isValidPhone } from '../../../../util/phone';
import { isValidEmail } from '../../../../util/email';
import { fileInputAccept } from '../../../../constants/photo-upload';
import { getFormikTouchedErrors } from '../../../../util/formik';
import Label from '../../../../components/global/Label';
import CustomControl from '../../../../components/global/CustomControl';
import {
  getIdentityFullName,
  getRosterIdentityClassName,
  getRosterIdentityContactMethods,
  getRosterIdentityGrade,
  getRosterIdentityLink,
  getRosterIdentityMainLinkedIdentity,
  getRosterIdentityOrganizationRefId,
  getRosterIdentityPrimaryEmail,
  getRosterIdentityTeacher,
} from '../../../../util/identity';
import FeatureBlock from '../../../../components/contextual/FeatureBlock';
import useBasename from '../../../../components/routing/useBasename';
import {
  canAccessNoPostToSocialOnBasename,
  canAccessSchoolFields,
  isYearbookEntity,
} from '../../../../util/product-permissions';
import { photoUploadFilter } from '../../../../util/photo-upload';
import BoxedCustomControl from '../../../../components/global/BoxedCustomControl';
import AlbumSearchSelect from './components/AlbumSearchSelect';
import DownloadPhotoButton from '../../../../containers/global/DownloadPhotoButtonContainer';
import uuidv4 from '../../../../util/uuid';
import { getThumbBySize } from '../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import RosterIdentityTagsDisplayBox from './components/RosterIdentityTagAssignment/RosterIdentityTagsDisplayBox';
import Select from '../../../../components/global/Select';
import { IDENTITY_GENDER } from '../../../../constants/identity';
import RosterIdentityTagAssignmentModal from './components/RosterIdentityTagAssignment/modal';
import RosterIdentityRosterQrAssignmentModal from './components/RosterIdentityRosterQrAssignment/modal';
import RosterIdentityQrCodeDownload from './components/RosterIdentityQrCodeDownload';
import RosterIdentityLinkedIdentityContactInfoModal from './components/RosterIdentityLinkedIdentityContactInfo/modal';
import { CONTACT_METHOD_LOCKED_COPY } from './components/RosterIdentityLinkedIdentityContactInfo/constants';
import { ACCOUNT_TYPE } from '../../views/AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';
import {
  HIERARCHY_GRADES_ENUM,
  HIERARCHY_GRADES_ENUM_LABELS,
} from '../../../../constants/hierarchy-grades';
import { isAlbum } from '../../../../util/album';
import { HierarchyItemForOwnerPurchaseJoins } from '../../../../graphql/queries/hierarchy-item';
import { MeWithRosterQrCodeBlanks } from '../../../../graphql/queries/account';
import { RosterIdentityForContactInfoEdits } from '../../../../graphql/queries/roster-identitiy';
import { AlbumFolderIdentityForRefresh } from '../../../../graphql/queries/album-folder';
import { formatPriceForDisplay } from '../../../../util/format-money';
import EditIcon from '../../../../components/icons/EditIcon';

const JOIN_TYPE = {
  ALBUM_FOLDER: 'ALBUM_FOLDER',
  ALBUMS: 'ALBUMS',
};

// Grades past 12th are no longer offered when assigning a member a grade here.
// They stay in the shared enum, which every other grade list still uses.
const HIDDEN_GRADES = [
  HIERARCHY_GRADES_ENUM.THIRTEENTH,
  HIERARCHY_GRADES_ENUM.FOURTEENTH,
  HIERARCHY_GRADES_ENUM.FIFTEENTH,
];

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  rosterIdentity: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    selfie_photo: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  selectedIdentity: PropTypes.shape({
    id: PropTypes.string,
    claimed: PropTypes.bool,
  }),
  identityId: PropTypes.string,
  loading: PropTypes.bool,
  photoUploadSuccess: PropTypes.func.isRequired,
  onRosterIdentityCreated: PropTypes.func,
  onRosterIdentityUpdated: PropTypes.func,
  defaultEditAddress: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  albumId: PropTypes.string,
  albumFolderId: PropTypes.string,
  albumFolderSelectionEnabled: PropTypes.bool,
  albumSelectionEnabled: PropTypes.bool,
  isSubscription: PropTypes.bool,
  organizationRefIdEnabled: PropTypes.bool,
  autoFocus: PropTypes.string,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      gender: PropTypes.string,
      uniform_enabled: PropTypes.bool,
      uniform_numbered: PropTypes.bool,
      levels: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            teams: PropTypes.shape({
              records: PropTypes.arrayOf(
                PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string,
                  coach_name: PropTypes.string,
                }),
              ),
            }),
          }),
        ),
      }),
    }),
  ),
  identityAlbumFolderTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      album_folder_tag: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        uniform_numbered: PropTypes.bool,
      }),
      jersey_number: PropTypes.string,
      uniform_album_folder_tag_level: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  pictureDayEnabled: PropTypes.bool,
};

const RosterIdentityUpdate = ({
  account,
  rosterIdentity,
  identityId,
  loading,
  photoUploadSuccess,
  onRosterIdentityCreated,
  onRosterIdentityUpdated,
  defaultEditAddress = false,
  onRequestClose,
  albumId,
  albumFolderId,
  albumFolderSelectionEnabled,
  albumSelectionEnabled,
  isSubscription,
  organizationRefIdEnabled = false,
  autoFocus = 'first_name',
  tags = [],
  identityAlbumFolderTags = [],
  selectedIdentity,
  pictureDayEnabled,
}) => {
  const basename = useBasename();
  const client = useApolloClient();

  const { data: { hierarchyItem } = {} } = useQuery(
    HierarchyItemForOwnerPurchaseJoins,
    {
      variables: { id: albumId ?? albumFolderId },
      skip: !albumId && !albumFolderId,
    },
  );

  const entity = hierarchyItem?.album ?? hierarchyItem?.album_folder;

  const isAlbumEntity = isAlbum(entity);
  const isYearbook = isYearbookEntity(entity);
  const ownerPurchasedJoinsEnabled = entity?.owner_purchased_joins_enabled;
  const isClaimed = !!selectedIdentity?.claimed || !!ownerPurchasedJoinsEnabled;

  // Individual roster QR code settings live on the album folder (event).
  // For an album entity we use its root album folder (mirrors the link
  // resolution in StandaloneAlbumCustomPrintConfigurator).
  const eventAlbumFolderId = isAlbumEntity
    ? entity?.root_album_folder?.id
    : albumFolderId;

  const rosterQrCodeSettings = isAlbumEntity
    ? entity?.root_album_folder?.roster_qr_code_settings
    : entity?.roster_qr_code_settings;

  const individualQrCodesEnabled = !!rosterQrCodeSettings?.enabled;

  // Find the roster identity link tied to this event's album folder.
  const rosterIdentityLink = rosterIdentity?.links?.records?.find(
    link => link?.album_folder?.id === eventAlbumFolderId,
  );

  const hasActiveRosterQrCode = !!rosterIdentityLink?.roster_qr_code?.id;

  const rosterQrCodeFileName = `${[
    rosterIdentity?.first_name,
    rosterIdentity?.last_name,
  ]
    .filter(Boolean)
    .join(' ')} QR code`.trim();

  // We can offer to attach a QR code blank when we have a roster identity on
  // an event that allows individual QR codes, the identity is linked to this
  // event, and it does not already have an active QR code.
  const isEligibleForRosterQrCode =
    !!rosterIdentity?.id &&
    individualQrCodesEnabled &&
    !!rosterIdentityLink &&
    !hasActiveRosterQrCode;

  // Fetch the count of clean (unassigned) blanks for this event so we know
  // whether there is a blank available to tie to this roster identity.
  const { data: { me: meWithRosterQrCodeBlanks } = {} } = useQuery(
    MeWithRosterQrCodeBlanks,
    {
      variables: {
        album_folder_id: eventAlbumFolderId,
        include_roster_qr_data: false,
      },
      skip: !isEligibleForRosterQrCode || !eventAlbumFolderId,
    },
  );

  const availableRosterQrCodeBlanks =
    meWithRosterQrCodeBlanks?.roster_qr_codes?.total ?? 0;

  // True when this roster identity is eligible and there is at least one
  // unassigned blank for this event that we can tie to it.
  const canConnectRosterIdentityToQrCode =
    isEligibleForRosterQrCode && availableRosterQrCodeBlanks > 0;

  // The per-account edit permissions are resolved on the fly, so they are kept
  // out of the fragments the member lists use and requested only here, where
  // they decide whether contact methods move to their own flow.
  const {
    data: { me: meWithContactMethods } = {},
    refetch: refetchContactMethods,
  } = useQuery(RosterIdentityForContactInfoEdits, {
    variables: { roster_identity_ids: [rosterIdentity?.id] },
    // The address-only flow opens straight into the address modal and never
    // renders the contact fields, so it has no use for these permissions.
    fetchPolicy: 'cache-and-network',
    skip: !rosterIdentity?.id || defaultEditAddress,
  });

  const [contactMethodsRosterIdentity] =
    meWithContactMethods?.roster_identities?.records ?? [];

  // Price used for consent requirement: album price_whole_album or albumFolder price
  const entityPrice =
    entity?.price_whole_album?.value ?? entity?.price?.value ?? 0;

  // Used to get base phone number & address:
  const mainLinkedIdentity =
    getRosterIdentityMainLinkedIdentity(rosterIdentity);
  const fileInput = useRef(null);
  const [fileUrl, setFileUrl] = useState('');
  const [editAddress, setEditAddress] = useState(defaultEditAddress);
  const [joinType, setJoinType] = useState(
    albumFolderSelectionEnabled ? JOIN_TYPE.ALBUM_FOLDER : JOIN_TYPE.ALBUMS,
  );
  const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [preCreateTags, setPreCreateTags] = useState([]);
  const [isTagSelectionModalOpen, setIsTagSelectionModalOpen] = useState(false);
  const [selectedTagForEdit, setSelectedTagForEdit] = useState(null);
  const [isRosterQrModalOpen, setIsRosterQrModalOpen] = useState(false);
  const [isContactInfoModalOpen, setIsContactInfoModalOpen] = useState(false);
  const [addedRosterQR, setAddedRosterQR] = useState(false);

  const isCreate = !rosterIdentity?.id;
  const isMemberCreate = isCreate && (albumFolderId || albumId);

  const {
    editableContactMethodCount,
    editablePhoneCount,
    editableEmailAccountCount,
    emailRows,
    phoneRows,
  } = getRosterIdentityContactMethods(contactMethodsRosterIdentity);

  // The inline fields are one phone and one email, and the two never feed each
  // other, so a member with exactly one of each is still fully represented by
  // them. Any other spread - two phones, two addresses, a longer list - needs
  // the dedicated flow to be shown honestly. The row counts matter as much as
  // the editable ones: a locked second phone has no inline field to sit in and
  // would otherwise be invisible.
  const contactMethodsFitInline =
    editablePhoneCount === 1 &&
    editableEmailAccountCount === 1 &&
    phoneRows.length === 1 &&
    emailRows.length <= 1;

  const manageContactMethodsEnabled =
    editableContactMethodCount >= 2 && !contactMethodsFitInline;

  // Read from the roster identity rather than from the permissions query: this
  // seeds the form's initial values, which are captured on the first render.
  const primaryEmail = getRosterIdentityPrimaryEmail(rosterIdentity);

  const storedEmailAddress = primaryEmail?.emailAddress;
  // Absent for a consumer's own address, which has no roster identity record
  // behind it to update - and which the field below never lets through anyway.
  const storedEmailAddressId = primaryEmail?.id;
  const storedPhoneNumber = mainLinkedIdentity?.phone_number;

  // A contact method that already has a value is only editable when the
  // platform says this PRO may change it. Empty ones stay open so a member
  // can still be given a first phone or email.
  const canUpdateInlinePhone =
    !storedPhoneNumber ||
    !!phoneRows.find(
      ({ linkedIdentityId }) => linkedIdentityId === mainLinkedIdentity?.id,
    )?.canUpdate;

  // Per address rather than per member: a consumer may have claimed this one
  // even though other addresses on the member are still the PRO's to change.
  // An address belonging to a consumer's account is never editable here - the
  // field only shows it, and a new one is added through the modal instead.
  //
  // Matched on the record rather than on its value. The address and the
  // permission come from two queries that settle separately, so a save that
  // changes the address leaves them disagreeing for a moment - and a value
  // match would read that disagreement as "not allowed" and lock the field.
  const canUpdateInlineEmail =
    !storedEmailAddress ||
    (!primaryEmail?.accountOwned &&
      !!emailRows.find(({ id }) => id === primaryEmail?.id)?.canUpdate);

  // Selfie filenames are only created on import (CSV) and the platform only
  // supports updating the existing records, so we can edit the ones we have
  // but never add or remove them here.
  const selfiePhotoFilenames = (
    rosterIdentity?.selfie_photo_filenames?.records ?? []
  )
    .filter(record => !!record?.id)
    .map(({ id, selfie_photo_filename: selfiePhotoFilename }) => ({
      id,
      selfie_photo_filename: selfiePhotoFilename ?? '',
    }));

  const isRosterIdentityTagsEnabled = tags.length > 0;
  const {
    updating,
    updateError,
    updateSuccess,
    onRosterIdentityUpdate,
    onTagsCreate,
    onTagsDelete,
  } = useRosterIdentityUpdate({
    onPhotoUploadSuccess: ({ photoId, file: uploadedFile }) =>
      photoUploadSuccess(
        {
          id: photoId,
          file: uploadedFile,
          identityId: rosterIdentity?.id,
        },
        true,
      ),
    currentClaimedIdentity: selectedIdentity?.claimed,
  });

  // "Staff" is listed first so it is reachable without scrolling the whole
  // grade list. The shared enum keeps its own order, which grade sorting relies on.
  const gradeOptions = useMemo(() => [
    HIERARCHY_GRADES_ENUM.STAFF,
    ...Object.values(HIERARCHY_GRADES_ENUM).filter(
      gradeEnum =>
        gradeEnum !== HIERARCHY_GRADES_ENUM.STAFF &&
        !HIDDEN_GRADES.includes(gradeEnum),
    ),
  ]);

  // A member already sitting on one of the retired grades keeps it as an
  // option, so editing them does not silently blank the field.
  const getGradeOptions = grade =>
    HIDDEN_GRADES.includes(grade) ? [...gradeOptions, grade] : gradeOptions;

  const onFileChoose = event => {
    event.preventDefault();
    fileInput.current.click();
  };

  const isFormDisabled = values => {
    // With contact methods handed off to their own flow, this form no longer
    // carries the fields that would satisfy the requirement.
    const hasContactInfo =
      manageContactMethodsEnabled ||
      values.phone_number?.trim() ||
      values.email_address?.trim();

    const purchaseConstraintsViolated =
      ownerPurchasedJoinsEnabled &&
      values.grant_purchase &&
      ((entityPrice > 0 && !values.collection_consent) ||
        (!isAlbumEntity &&
          !!albumFolderId &&
          (selectedAlbums?.length ?? 0) === 0));

    return (
      !values.first_name ||
      !values.last_name ||
      !hasContactInfo ||
      updating ||
      purchaseConstraintsViolated
    );
  };

  const openTagModal = (tag = null) => {
    setSelectedTagForEdit(tag);
    setIsTagSelectionModalOpen(true);
  };

  const closeTagModal = () => {
    setIsTagSelectionModalOpen(false);
    setSelectedTagForEdit(null);
  };

  const handleTagsCreate = async (input, context) => {
    if (isCreate) {
      const updatedTag = selectedTagForEdit
        ? input?.album_folder_tags?.[0]
        : null;

      const updatedTags = [...preCreateTags].map(existingTag => {
        if (updatedTag && selectedTagForEdit.id === existingTag.id) {
          return {
            ...existingTag,
            album_folder_tag: {
              id: updatedTag.album_folder_tag_id,
              type: updatedTag.type,
              uniform_numbered: updatedTag.uniform_numbered,
            },
            jersey_number: updatedTag.uniform?.jersey_number,
            uniform_album_folder_tag_level: updatedTag.uniform
              ?.uniform_album_folder_tag_level_id
              ? {
                  id: updatedTag.uniform.uniform_album_folder_tag_level_id,
                  ...(updatedTag?.level ?? {}),
                }
              : null,
            uniform_album_folder_tag_level_team: updatedTag.uniform
              ?.uniform_album_folder_tag_level_team_id
              ? {
                  id: updatedTag.uniform.uniform_album_folder_tag_level_team_id,
                  ...(updatedTag?.team ?? {}),
                }
              : null,
          };
        }

        return existingTag;
      });

      const newTags = updatedTag
        ? []
        : input.album_folder_tags
            .map(tag => {
              if (updatedTags.find(({ id }) => id === tag.id)) {
                return null;
              }

              return {
                id: uuidv4(),
                album_folder_tag: {
                  id: tag.album_folder_tag_id,
                  type: tag.type,
                  uniform_numbered: tag.uniform_numbered,
                },
                jersey_number: tag.uniform?.jersey_number,
                uniform_album_folder_tag_level: tag.uniform
                  ?.uniform_album_folder_tag_level_id
                  ? {
                      id: tag.uniform.uniform_album_folder_tag_level_id,
                      ...(tag?.level ?? {}),
                    }
                  : null,
                uniform_album_folder_tag_level_team: tag.uniform
                  ?.uniform_album_folder_tag_level_team_id
                  ? {
                      id: tag.uniform.uniform_album_folder_tag_level_team_id,
                      ...(tag?.team ?? {}),
                    }
                  : null,
              };
            })
            .filter(Boolean);

      setPreCreateTags([...updatedTags, ...newTags]);
    } else {
      // In update mode, call the API directly
      await onTagsCreate(input, context);
    }
  };

  const handleTagsDelete = async (tag, context) => {
    if (isCreate) {
      setPreCreateTags(
        preCreateTags.filter(existingTag => existingTag.id !== tag.id),
      );
    } else {
      await onTagsDelete(tag.id, context);
    }
  };

  const onFormSubmit = async values => {
    if (isFormDisabled(values)) {
      return;
    }

    const {
      address,
      phone_number: phoneNumber,
      gender,
      grade,
      selfie,
      grant_purchase: grantPurchase,
      collection_consent: collectionConsent,
      selfie_photo_filenames: updatedSelfiePhotoFilenames,
      ...restValues
    } = values;

    // The phone input seeds a country code, so an untouched field is not a
    // number the platform should be asked to store.
    const sanitizedPhoneNumber = isBlankPhone(phoneNumber) ? null : phoneNumber;

    // On update the number is only written when it actually changed, so that
    // an untouched form never calls the contact method mutation. Creates
    // always carry it, since there is nothing to compare against.
    const phoneNumberForSubmit =
      isCreate || (sanitizedPhoneNumber ?? null) !== (storedPhoneNumber ?? null)
        ? sanitizedPhoneNumber
        : null;

    // Same for the address: sending an unchanged one would rewrite the record
    // on every save, whatever else the PRO was editing.
    const trimmedEmailAddress = values.email_address?.trim();
    // Gated on the permission as well as on the value: a consumer's own address
    // sits in this field read only, and writing it back would copy it onto the
    // roster identity as an address of its own.
    const emailAddressChanged =
      canUpdateInlineEmail &&
      (trimmedEmailAddress ?? '') !== (storedEmailAddress ?? '');

    const contactMethodChanged = !!phoneNumberForSubmit || emailAddressChanged;

    const purchasedAlbumIds = (() => {
      if (!ownerPurchasedJoinsEnabled || !grantPurchase) {
        return [];
      }

      return isAlbumEntity ? [albumId] : selectedAlbums.map(({ id }) => id);
    })();

    const isMarketingAccount =
      mainLinkedIdentity?.account?.type === ACCOUNT_TYPE.MARKETING;

    // A linked identity whose account has an email but no phone belongs to a
    // consumer who claimed it with just an email - the same as a marketing
    // placeholder, its phone slot is not this PRO's to write to. Sending the
    // new number to that id updates a real, already-authenticated account and
    // the platform rejects it as insufficient privileges, so a fresh linked
    // identity is created for the number instead.
    const mainLinkedIdentityHasNoPhoneSlot =
      isMarketingAccount ||
      (!!mainLinkedIdentity?.account?.email_address && !storedPhoneNumber);

    const rosterIdentityId = rosterIdentity?.id;
    const selectedIdentityId = selectedIdentity?.id;

    const payload = {
      ...restValues,
      // The empty option on the grade select is the absence of a grade, not a
      // grade the platform can store - its enum has no member for it.
      grade: grade || null,
      // The school fields are read from the link for this event, so that is
      // where a save has to write them back. Without it they would land on the
      // roster identity itself and the link's older values would keep winning.
      roster_identity_link_id: getRosterIdentityLink({
        rosterIdentity,
        albumId,
        albumFolderId,
      })?.id,
      date_of_birth: restValues.date_of_birth
        ? format(restValues.date_of_birth, 'yyyy-MM-dd')
        : null,
      identity_id: identityId,
      account_id: account.id,
      selected_identity_id: selectedIdentityId,
      email_address: emailAddressChanged ? trimmedEmailAddress : undefined,
      // Tells the hook to edit the existing address rather than add a second.
      email_address_id: emailAddressChanged ? storedEmailAddressId : undefined,
      roster_identity_id: rosterIdentityId,
      linked_identities: [
        {
          id:
            !mainLinkedIdentityHasNoPhoneSlot || !!address?.address_line_1
              ? mainLinkedIdentity?.id
              : undefined,
          phone_number: phoneNumberForSubmit,
          addresses: address?.address_line_1 ? [address] : [],
        },
      ],
      linked_album_ids: (() => {
        if (!(albumId || joinType === JOIN_TYPE.ALBUMS)) {
          return [];
        }

        return albumId ? [albumId] : selectedAlbums.map(({ id }) => id);
      })(),
      linked_album_folder_ids:
        albumFolderId && joinType === JOIN_TYPE.ALBUM_FOLDER
          ? [albumFolderId]
          : [],
      owner_purchased_album_ids: purchasedAlbumIds,
      selfie,
      create_album_folder_tags: isCreate ? preCreateTags : undefined,
    };

    if (gender) {
      payload.gender = gender;
    }

    // Blanking a filename is not an update the platform can perform, so only
    // send the ones that actually changed to a new value.
    const changedSelfiePhotoFilenames = (
      updatedSelfiePhotoFilenames ?? []
    ).filter(({ id, selfie_photo_filename: filename }) => {
      const trimmedFilename = (filename ?? '').trim();

      return (
        !!trimmedFilename &&
        trimmedFilename !==
          selfiePhotoFilenames.find(record => record.id === id)
            ?.selfie_photo_filename
      );
    });

    if (changedSelfiePhotoFilenames.length > 0) {
      payload.update_selfie_photo_filenames = changedSelfiePhotoFilenames.map(
        ({ id, selfie_photo_filename: filename }) => ({
          roster_identity_selfie_photo_filename_id: id,
          selfie_photo_filename: filename.trim(),
        }),
      );
    }

    const latestRosterIdentity = await onRosterIdentityUpdate(payload);

    if (!latestRosterIdentity) {
      // Possible error
      return;
    }

    // Saving a contact method can attach a whole new account to the member, so
    // the permissions that decide whether these fields stay editable have to
    // be resolved again rather than read from the pre-save answer. Nothing
    // else on this form can change them.
    if (rosterIdentity?.id && contactMethodChanged) {
      await refetchContactMethods();
    }

    if (isCreate) {
      onRosterIdentityCreated?.(latestRosterIdentity);
      onRequestClose();
    } else {
      onRosterIdentityUpdated?.(latestRosterIdentity);

      // The Gallery Members grid reads this row from a separate, list-scoped
      // query, so saving here doesn't touch its cache on its own - re-request
      // just this one row (by the same Identity id the grid already keyed it
      // by) rather than refetching the whole list. Apollo's normalized cache
      // merges the fresh fields straight into the row the grid is watching.
      if (albumFolderId && identityId) {
        client
          .query({
            query: AlbumFolderIdentityForRefresh,
            variables: {
              id: albumFolderId,
              album_folder_id: albumFolderId,
              identityIds: [identityId],
            },
            fetchPolicy: 'network-only',
          })
          .catch(() => {});
      }

      // If editing only the address, close the modal immediately after save
      if (defaultEditAddress) {
        onRequestClose();
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  const photo = fileUrl
    ? {
        thumbnail_urls: [{ width: 0, height: 0, url: fileUrl }],
        id: 'local-upload',
      }
    : (rosterIdentity?.selfie_photo ??
      rosterIdentity?.current_matching_photo ??
      rosterIdentity?.selfie_photo_urls?.records?.[0]?.selfie_photo ??
      rosterIdentity?.selfie_photo_filenames?.records?.[0]?.selfie_photo ??
      mainLinkedIdentity?.identity?.current_photo);

  const photoThumbnail = getThumbBySize(
    photo?.thumbnail_urls ?? [],
    IMAGE_SIZE_CODE.MEDIUM,
  );

  const mainLinkedIdentityPhotoOutdated =
    mainLinkedIdentity?.identity?.has_outdated_selfie;

  const validate = values => {
    const errors = {};

    if (values.email_address && !isValidEmail(values.email_address, true)) {
      errors.email_address = 'Please provide a valid email address.';
    }

    if (
      !mainLinkedIdentity?.id &&
      !!values?.address?.address_line_1 &&
      !isValidPhone(values.phone_number)
    ) {
      errors.phone_number =
        'You must provide a valid phone number when creating an address for an identity.';
    }

    if (
      values.phone_number &&
      !/^\+\d+$/.test(values.phone_number) && // If not just country code
      !isValidPhone(values.phone_number)
    ) {
      errors.phone_number =
        'Provided phone number is not valid. Please provide a valid one.';
    }

    const requiresRefId =
      isYearbook && isCreate && values.grade !== HIERARCHY_GRADES_ENUM.STAFF;

    if (requiresRefId && !(values.organization_ref_id ?? '').trim()) {
      errors.organization_ref_id =
        'Except for STAFF members, Ref ID is required for all yearbook event members.';
    }

    return errors;
  };

  const photoContainerClasses = classNames({
    'text-center': true,
    'mb-3': !photo?.id,
  });

  // Hide join toggle for now since is confusing for PROs
  const shouldHideJoinToggle = classNames({
    'd-none': true,
  });

  return (
    <Formik
      validate={validate}
      initialValues={{
        selfie: null,
        first_name: rosterIdentity?.first_name ?? '',
        last_name: rosterIdentity?.last_name ?? '',
        gender: rosterIdentity?.gender ?? '',
        phone_number: mainLinkedIdentity?.phone_number ?? '',
        address: mainLinkedIdentity?.addresses?.records[0] ?? {
          // Pass the name of the identity
          // as the default on an empty address
          // so that it's prepopulated.
          name: getIdentityFullName(rosterIdentity),
        },
        date_of_birth: rosterIdentity?.date_of_birth
          ? parse(rosterIdentity.date_of_birth, 'yyyy-MM-dd', new Date())
          : null,
        photo_share_blocked: rosterIdentity?.photo_share_blocked,
        organization_ref_id: getRosterIdentityOrganizationRefId({
          rosterIdentity,
          albumId,
          albumFolderId,
        }),
        email_address: storedEmailAddress,
        grade: getRosterIdentityGrade({
          rosterIdentity,
          albumId,
          albumFolderId,
          rawValue: true,
        }),
        teacher_name: getRosterIdentityTeacher({
          rosterIdentity,
          albumId,
          albumFolderId,
        }),
        class_name: getRosterIdentityClassName({
          rosterIdentity,
          albumId,
          albumFolderId,
        }),
        claimed_identity: isClaimed,
        grant_purchase: false,
        collection_consent: false,
        selfie_photo_filenames: selfiePhotoFilenames,
      }}
      onSubmit={onFormSubmit}
      render={({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        touched,
        dirty,
      }) => {
        const formikErrors = Object.values(
          getFormikTouchedErrors(errors, touched),
        );
        const usedTagIds =
          identityAlbumFolderTags &&
          identityAlbumFolderTags.map(tag => tag.album_folder_tag.id);

        const availableTags =
          tags &&
          tags.filter(
            tag => tag.gender === values.gender && !usedTagIds.includes(tag.id),
          );

        // Rendered both at the top and at the bottom of the form so it is
        // reachable without scrolling.
        const renderSubmitButton = className => (
          <div className={classNames('text-right', className)}>
            <Button
              type="submit"
              disabled={!dirty || isFormDisabled(values)}
              btnSize="sm"
              className="m-0"
            >
              {(() => {
                if (updating) {
                  return 'Loading...';
                }

                return rosterIdentity ? 'Update' : 'Create';
              })()}
            </Button>
          </div>
        );

        return (
          <Fragment>
            <form method="POST" onSubmit={handleSubmit}>
              {renderSubmitButton('mb-2')}
              <div className={photoContainerClasses}>
                {(photoThumbnail || hasActiveRosterQrCode) && (
                  <p className="my-1 text-xs text-normal text-light">
                    {photoThumbnail && 'Hover to enlarge reference photo'}
                    {photoThumbnail && hasActiveRosterQrCode && <br />}
                    {hasActiveRosterQrCode &&
                      'Click on the QR code to download it'}
                  </p>
                )}
                <div className="d-flex justify-content-center align-items-start">
                  <a onClick={onFileChoose} href="#">
                    <IdentityAvatarContainer
                      size={AVATAR_SIZE_72}
                      identity={{
                        id:
                          fileUrl || !rosterIdentity?.id
                            ? uuidv4()
                            : rosterIdentity.id,
                        ...(rosterIdentity || {}),
                        name: rosterIdentity?.first_name,
                        current_matching_photo: photo,
                      }}
                      selfieType="matching"
                      data-tip=""
                      data-for={`tooltip-identity-selfie-${rosterIdentity?.id}`}
                      data-place="top"
                    >
                      {photoThumbnail && (
                        <ReactTooltip
                          delayHide={250}
                          className="member-photos-tooltip"
                          id={`tooltip-identity-selfie-${rosterIdentity?.id}`}
                          backgroundColor="transparent"
                          arrowColor="transparent"
                          effect="solid"
                        >
                          <img src={photoThumbnail.url} alt="Identity Selfie" />
                        </ReactTooltip>
                      )}
                    </IdentityAvatarContainer>
                  </a>
                  {hasActiveRosterQrCode && (
                    <div className="ml-1">
                      <RosterIdentityQrCodeDownload
                        rosterQrCodeId={rosterIdentityLink.roster_qr_code.id}
                        fileName={rosterQrCodeFileName}
                      />
                    </div>
                  )}
                </div>
                <a
                  onClick={onFileChoose}
                  href="#"
                  className="d-block mt-1 text-sm"
                >
                  {photo ? 'Edit' : 'Add'} reference photo
                </a>
                {mainLinkedIdentityPhotoOutdated && photo && (
                  <p className="text-danger text-xs">
                    The selfie shown for this identity is outdated and <br />
                    won&rsquo;t be used for matching. Please update it to
                    restore matching.
                  </p>
                )}
              </div>
              {photo?.id && (
                <div className="text-center mb-3">
                  <DownloadPhotoButton
                    downloadOriginal={!!photo?.is_owner}
                    photoId={photo?.id}
                    photoSize={IMAGE_SIZE_CODE.LARGE2X}
                    btnStyle="link"
                    btnSize="sm"
                  >
                    Download current reference photo
                  </DownloadPhotoButton>
                </div>
              )}

              <input
                id="upload-photo"
                type="file"
                accept={fileInputAccept}
                onChange={event => {
                  const [eventFile] = event.target.files;

                  if (!photoUploadFilter(eventFile)) {
                    return;
                  }

                  setFileUrl(URL.createObjectURL(eventFile));

                  handleChange({
                    target: {
                      name: 'selfie',
                      value: eventFile,
                    },
                  });
                }}
                ref={fileInput}
                hidden
                data-testid="roster-identity-update-upload-photo"
              />

              {canConnectRosterIdentityToQrCode && !addedRosterQR && (
                <Alert
                  type="info"
                  className="w-100 d-flex justify-content-between align-items-center"
                >
                  This event is set up to use Individual QR Codes but this
                  identity is not associated to any QR Code.
                  <Button
                    btnStyle="link"
                    btnSize="sm"
                    onClick={() => setIsRosterQrModalOpen(true)}
                  >
                    <EditIcon size="15px" id="qr-code-link-edit" />
                  </Button>
                </Alert>
              )}

              <div className="row">
                <div className="col-6">
                  <FormGroup
                    id="first_name"
                    name="first_name"
                    label="First Name"
                    value={values.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'first_name'}
                    autoComplete="off"
                  />
                </div>

                <div className="col-6">
                  <FormGroup
                    id="last_name"
                    name="last_name"
                    label="Last Name"
                    value={values.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'last_name'}
                    autoComplete="off"
                  />
                </div>
                <div className="col-6">
                  <Select
                    id="gender"
                    label="Gender"
                    value={values.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'gender'}
                    autoComplete="off"
                  >
                    <option value="">Select</option>
                    {Object.values(IDENTITY_GENDER).map(gender => (
                      <option key={gender} value={gender}>
                        {gender}
                      </option>
                    ))}
                  </Select>
                </div>
                {!isMemberCreate && (
                  <div className="col-6">
                    <div className="form-group">
                      <Label id="birthDate" labelNote="optional">
                        Birthday
                      </Label>

                      <DatePicker
                        id="birthDate"
                        name="date_of_birth"
                        className="form-control"
                        isClearable
                        maxDate={new Date()}
                        selected={values.date_of_birth}
                        onChange={date => {
                          date?.setHours(0, 0, 0, 0);

                          handleChange({
                            target: {
                              name: 'date_of_birth',
                              value: date,
                            },
                          });

                          handleBlur?.({
                            target: {
                              name: 'date_of_birth',
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
                {manageContactMethodsEnabled ? (
                  <div className="col-12 form-group">
                    <Label id="contact-methods">Contact methods</Label>
                    <Button
                      btnStyle="link"
                      btnSize="sm"
                      className="p-0 d-block"
                      onClick={() => setIsContactInfoModalOpen(true)}
                    >
                      Manage contact methods
                    </Button>
                  </div>
                ) : (
                  <Fragment>
                    <div className="col-6">
                      <FormGroup
                        id="email_address"
                        label="Email"
                        value={values.email_address}
                        onChange={handleChange}
                        disabled={!canUpdateInlineEmail}
                        onBlur={handleBlur}
                        autoFocus={autoFocus === 'email_address'}
                        autoComplete="off"
                      />
                    </div>

                    <div className="col-6">
                      <PhoneInput
                        id="phone_number"
                        label="Mobile Phone Number"
                        value={values.phone_number || '+1'}
                        onChange={phone => {
                          handleChange({
                            target: {
                              name: 'phone_number',
                              value: phone,
                            },
                          });
                        }}
                        required={isCreate || !!values.address}
                        disabled={!canUpdateInlinePhone}
                        onBlur={handleBlur}
                        autoFocus={autoFocus === 'phone_number'}
                        autoComplete="off"
                      />
                    </div>

                    {(!canUpdateInlineEmail || !canUpdateInlinePhone) && (
                      <div className="col-12">
                        <p className="text-light text-xs">
                          {CONTACT_METHOD_LOCKED_COPY}
                        </p>
                      </div>
                    )}

                    {/*
                      These two fields are the only ones the member form has,
                      so once they hold a method there is nowhere left to put
                      another. Empty fields need no CTA - they are the place to
                      type one.
                    */}
                    {!isCreate &&
                      (!!values.email_address?.trim() ||
                        !isBlankPhone(values.phone_number)) && (
                        <div className="col-12">
                          <Button
                            btnStyle="link"
                            btnSize="sm"
                            className="p-0"
                            onClick={() => setIsContactInfoModalOpen(true)}
                          >
                            Add a new contact method
                          </Button>
                        </div>
                      )}
                  </Fragment>
                )}
                {(organizationRefIdEnabled ||
                  (isYearbook &&
                    isCreate &&
                    values.grade !== HIERARCHY_GRADES_ENUM.STAFF)) && (
                  <div className="col-12">
                    <FormGroup
                      id="organization_ref_id"
                      name="organization_ref_id"
                      label="Ref ID"
                      value={values.organization_ref_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus={autoFocus === 'organization_ref_id'}
                    />
                  </div>
                )}
                {gradeOptions.length > 0 && canAccessSchoolFields(basename) && (
                  <Fragment>
                    <div className="w-100" />
                    <div className="col-6 col-md-4">
                      <Label id="grade">Grade</Label>
                      <select
                        id="grade"
                        name="grade"
                        value={values.grade}
                        className="form-control"
                        onChange={handleChange}
                        placeholder="Select a grade or staff"
                      >
                        <option value="">Select a grade or staff</option>
                        {getGradeOptions(values.grade).map(gradeEnum => (
                          <option key={gradeEnum} value={gradeEnum}>
                            {HIERARCHY_GRADES_ENUM_LABELS[gradeEnum]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 col-md-5">
                      <FormGroup
                        id="teacher_name"
                        name="teacher_name"
                        label="Home Room Teacher"
                        value={values.teacher_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus={autoFocus === 'teacher_name'}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <FormGroup
                        id="class_name"
                        name="class_name"
                        label="Home room #"
                        value={values.class_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus={autoFocus === 'class_name'}
                      />
                    </div>
                  </Fragment>
                )}
                {(values.selfie_photo_filenames ?? []).map(
                  ({ id, selfie_photo_filename: filename }, index) => (
                    <div className="col-6" key={id}>
                      <FormGroup
                        id={`selfie_photo_filename-${index}`}
                        name={`selfie_photo_filenames[${index}].selfie_photo_filename`}
                        label={`Image Filename${
                          values.selfie_photo_filenames.length > 1
                            ? ` ${index + 1}`
                            : ''
                        }`}
                        value={filename}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus={
                          autoFocus === 'selfie_photo_filename' && index === 0
                        }
                        autoComplete="off"
                      />
                    </div>
                  ),
                )}
                <FeatureBlock
                  condition={canAccessNoPostToSocialOnBasename(basename)}
                >
                  <div className="col-6">
                    <div className="form-group">
                      <Label id="noPostToSocial">No post to social</Label>
                      <CustomControl
                        type="checkbox"
                        id="npts"
                        size="sm"
                        checked={values.photo_share_blocked}
                        onChange={() =>
                          handleChange({
                            target: {
                              name: 'photo_share_blocked',
                              value: !values.photo_share_blocked,
                            },
                          })
                        }
                        label="Do not allow photos featuring this member to be posted to social via apps."
                      />
                    </div>
                  </div>
                </FeatureBlock>
                <FeatureBlock
                  condition={
                    !ownerPurchasedJoinsEnabled &&
                    (selectedIdentity || isMemberCreate)
                  }
                >
                  <div className="col-12">
                    <div className="form-group">
                      <Label id="claimIdentity">Claimed Identity</Label>
                      <CustomControl
                        type="checkbox"
                        id="claimIdentity"
                        size="sm"
                        checked={values.claimed_identity}
                        onChange={() =>
                          handleChange({
                            target: {
                              name: 'claimed_identity',
                              value: !values.claimed_identity,
                            },
                          })
                        }
                        label="Member will receive notifications for matches. Email-only members will always be claimed in order to receive email notifications."
                      />
                    </div>
                  </div>
                </FeatureBlock>
              </div>
              {albumFolderId && pictureDayEnabled && (
                <div className={shouldHideJoinToggle}>
                  <div className="row mb-2">
                    {albumFolderSelectionEnabled && (
                      <div className="col-12">
                        <BoxedCustomControl
                          id="album-folder-join"
                          type="checkbox"
                          label="Join the gallery"
                          description="Join all albums within this gallery."
                          onChange={() => {
                            setJoinType(JOIN_TYPE.ALBUM_FOLDER);
                          }}
                          checked={joinType === JOIN_TYPE.ALBUM_FOLDER}
                        />
                      </div>
                    )}
                  </div>
                  {albumFolderSelectionEnabled && albumSelectionEnabled && (
                    <div className="col-12">
                      <Label
                        id="label-or"
                        labelType="expanded"
                        className="w-100 text-center my-1"
                      >
                        OR
                      </Label>
                    </div>
                  )}
                  {albumSelectionEnabled && (
                    <div className="col-12">
                      <BoxedCustomControl
                        id="album-join"
                        type="checkbox"
                        label="Join albums"
                        description={`Specify the albums you want this person to be ${
                          isSubscription
                            ? 'subscribed to'
                            : 'able to preview their photos on'
                        }.`}
                        onChange={() => {
                          setJoinType(JOIN_TYPE.ALBUMS);
                        }}
                        checked={joinType === JOIN_TYPE.ALBUMS}
                      >
                        {joinType === JOIN_TYPE.ALBUMS && (
                          <AlbumSearchSelect
                            selectedAlbums={selectedAlbums}
                            albumFolderId={albumFolderId}
                            onSelectedAlbumsChange={newSelectedAlbums =>
                              setSelectedAlbums(newSelectedAlbums)
                            }
                          />
                        )}
                      </BoxedCustomControl>
                    </div>
                  )}
                </div>
              )}

              {ownerPurchasedJoinsEnabled && (
                <div className="row mb-1">
                  <div className="col-12">
                    <Label id="grant-purchase">Grant purchase</Label>
                    <CustomControl
                      type="checkbox"
                      id="grant_purchase"
                      size="sm"
                      checked={values.grant_purchase}
                      onChange={() => {
                        const next = !values.grant_purchase;
                        handleChange({
                          target: {
                            name: 'grant_purchase',
                            value: !values.grant_purchase,
                          },
                        });
                        if (next && entityPrice === 0) {
                          handleChange({
                            target: {
                              name: 'collection_consent',
                              value: true,
                            },
                          });
                        }
                        if (!next) {
                          handleChange({
                            target: {
                              name: 'collection_consent',
                              value: false,
                            },
                          });
                        }
                      }}
                      label={
                        isAlbumEntity
                          ? 'Grant purchase to consumer for this album'
                          : 'Grant purchase to consumer for selected albums'
                      }
                    />
                  </div>

                  {values.grant_purchase && !isAlbumEntity && albumFolderId && (
                    <div className="col-12 mb-1">
                      <AlbumSearchSelect
                        selectedAlbums={selectedAlbums}
                        albumFolderId={albumFolderId}
                        onSelectedAlbumsChange={newSelectedAlbums =>
                          setSelectedAlbums(newSelectedAlbums)
                        }
                      />
                    </div>
                  )}

                  {values.grant_purchase && entityPrice > 0 && (
                    <div className="col-12 mb-1">
                      <CustomControl
                        id="collection-consent"
                        type="checkbox"
                        name="collection_consent"
                        checked={values.collection_consent}
                        onClick={() =>
                          handleChange({
                            target: {
                              name: 'collection_consent',
                              value: !values.collection_consent,
                            },
                          })
                        }
                        label={
                          <span>
                            I have collected{' '}
                            <span className="text-extra-bold">
                              ${formatPriceForDisplay(entityPrice)}
                            </span>{' '}
                            directly from{' '}
                            <span className="text-primary">
                              {values.first_name ?? 'this person'}
                            </span>{' '}
                            and am responsible for Waldo&apos;s fee for this
                            subscription
                          </span>
                        }
                        size="sm"
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              )}

              {isRosterIdentityTagsEnabled && (
                <RosterIdentityTagsDisplayBox
                  tags={tags}
                  className="mb-2"
                  identityAlbumFolderTags={identityAlbumFolderTags}
                  preCreateAlbumFolderTags={preCreateTags}
                  sportsTagsDelete={handleTagsDelete}
                  albumFolderId={albumFolderId}
                  identityId={identityId}
                  mainLinkedIdentity={mainLinkedIdentity}
                  gender={values.gender}
                  onEditSportsTag={openTagModal}
                />
              )}

              <UserAddressDisplayEditBox
                address={values.address}
                onClick={() => {
                  setEditAddress(true);
                }}
                className="form-group"
              />

              {(updateError || formikErrors.length > 0) && (
                <Alert type="danger">
                  {updateError ||
                    formikErrors.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                </Alert>
              )}

              {updateSuccess && (
                <Alert type="success" dismissible>
                  {isCreate ? 'Created' : 'Updated'} successfully!
                </Alert>
              )}

              {renderSubmitButton()}
            </form>

            <RosterIdentityTagAssignmentModal
              forceOpen={isTagSelectionModalOpen}
              albumFolderId={albumFolderId}
              identityId={identityId}
              tags={selectedTagForEdit ? tags : availableTags}
              selectedTag={selectedTagForEdit}
              identityAlbumFolderTags={
                selectedTagForEdit ? [selectedTagForEdit] : []
              }
              onComplete={() => {
                // In update mode, save roster identity right away if gender is selected
                // In create mode, don't trigger handleSubmit - tags are saved locally
                if (!isCreate && values.gender !== rosterIdentity?.gender) {
                  handleSubmit();
                }
                closeTagModal();
              }}
              sportsTagsCreate={handleTagsCreate}
              onCancel={closeTagModal}
              includeTeamLevelData={isCreate}
            />

            <RosterIdentityRosterQrAssignmentModal
              forceOpen={isRosterQrModalOpen}
              albumFolderId={eventAlbumFolderId}
              rosterIdentityId={rosterIdentity?.id}
              rosterIdentityLinkId={rosterIdentityLink?.id}
              onCancel={() => setIsRosterQrModalOpen(false)}
              onComplete={() => {
                setIsRosterQrModalOpen(false);
                setAddedRosterQR(true);
              }}
            />

            <RosterIdentityLinkedIdentityContactInfoModal
              forceOpen={isContactInfoModalOpen}
              rosterIdentityId={rosterIdentity?.id}
              onCancel={() => setIsContactInfoModalOpen(false)}
              onComplete={() => setIsContactInfoModalOpen(false)}
            />

            <UserAddressFormModal
              forceOpen={editAddress}
              address={values.address}
              onCancel={() => {
                setEditAddress(false);

                if (defaultEditAddress) {
                  onRequestClose();
                }
              }}
              onSuccess={address => {
                setEditAddress(false);
                handleChange({
                  target: {
                    name: 'address',
                    value: address,
                  },
                });

                if (!isCreate) {
                  // When updating, save right away:
                  handleSubmit();
                }
              }}
            />
          </Fragment>
        );
      }}
    />
  );
};

RosterIdentityUpdate.propTypes = propTypes;

export default RosterIdentityUpdate;
